import React, { useState } from 'react';
import { CheckCircle2, Copy, GraduationCap, Eye, Loader2, PenSquare } from 'lucide-react';
import { createClass } from '../../lib/api';
import { toast } from 'sonner';

/**
 * Teacher first-run 3-step setup card (master spec Phase 2 item 1).
 * Not a modal tour — a card at the top of DraftBoard that disappears
 * forever once all three steps are done.
 *
 * Step completion: step 1 and the class half of step 2 derive from real data
 * (assignments/classes exist); the "seen student view" and "link shared"
 * halves persist per-teacher in localStorage.
 */

const s2Key = (id) => `draftflow_setup_s2_${id}`;
const s3Key = (id) => `draftflow_setup_s3_${id}`;
const doneKey = (id) => `draftflow_setup_done_${id}`;

export const isSetupDismissed = (profileId) => {
  try { return localStorage.getItem(doneKey(profileId)) === '1'; } catch { return false; }
};

export const SetupCard = ({
  profile,
  assignments,
  classes,
  gcConnected,
  onCreateAssignment,
  onGoToClassroom,
  onClassCreated,
}) => {
  const [creatingClass, setCreatingClass] = useState(false);
  const [, forceRender] = useState(0);

  const pid = profile?.id;
  const step1 = assignments.length > 0;
  const step2 =
    classes.length > 0 || gcConnected === true ||
    (() => { try { return localStorage.getItem(s2Key(pid)) === '1'; } catch { return false; } })();
  const step3 = (() => { try { return localStorage.getItem(s3Key(pid)) === '1'; } catch { return false; } })();

  if (isSetupDismissed(pid)) return null;
  if (step1 && step2 && step3) {
    try { localStorage.setItem(doneKey(pid), '1'); } catch {}
    return null;
  }

  const markStep = (key) => {
    try { localStorage.setItem(key, '1'); } catch {}
    forceRender(n => n + 1);
  };

  const copyJoinLink = async () => {
    if (classes.length > 0) {
      const link = `${window.location.origin}/join/class/${classes[0].join_code}`;
      await navigator.clipboard.writeText(link);
      toast.success('Join link copied! Share it with your students.');
      markStep(s2Key(pid));
      return;
    }
    // No class yet — create one, then copy its link in the same click
    setCreatingClass(true);
    try {
      const cls = await createClass(`${(profile?.full_name || 'My').split(' ')[0]}'s Class`);
      const link = `${window.location.origin}/join/class/${cls.join_code}`;
      await navigator.clipboard.writeText(link);
      toast.success('Class created and join link copied! Share it with your students.');
      markStep(s2Key(pid));
      onClassCreated?.(cls);
    } catch (e) {
      console.error('Class creation failed:', e);
      toast.error("Couldn't create your class. Try again in a moment.");
    } finally {
      setCreatingClass(false);
    }
  };

  const openStudentView = () => {
    if (!step1) return;
    window.open(`/write/${assignments[0].id}?preview=1`, '_blank', 'noopener');
    markStep(s3Key(pid));
  };

  const steps = [
    {
      n: 1,
      done: step1,
      title: 'Create your first assignment',
      detail: 'Start from a ready-to-teach argumentative essay template — edit anything, then save.',
      action: (
        <button
          data-testid="setup-create-assignment"
          onClick={onCreateAssignment}
          className="btn-teal px-4 py-2 rounded-lg flex items-center gap-2 text-sm"
        >
          <PenSquare size={16} />
          Create assignment
        </button>
      ),
    },
    {
      n: 2,
      done: step2,
      title: 'Get students in',
      detail: 'Share a join link with your students, or connect Google Classroom to import a roster.',
      action: (
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="setup-copy-join-link"
            onClick={copyJoinLink}
            disabled={creatingClass}
            className="btn-teal px-4 py-2 rounded-lg flex items-center gap-2 text-sm disabled:opacity-60"
          >
            {creatingClass ? <Loader2 size={16} className="animate-spin" /> : <Copy size={16} />}
            {classes.length > 0 ? 'Copy join link' : 'Create class & copy link'}
          </button>
          <button
            data-testid="setup-connect-classroom"
            onClick={onGoToClassroom}
            className="border border-border-secondary text-cream hover:border-flow-blue-hover px-4 py-2 rounded-lg flex items-center gap-2 text-sm transition-colors"
          >
            <GraduationCap size={16} />
            Connect Google Classroom
          </button>
        </div>
      ),
    },
    {
      n: 3,
      done: step3,
      title: 'See what students see',
      detail: step1
        ? 'Open the student writing view of your assignment — coach and all.'
        : 'Create your first assignment to unlock the student view.',
      action: (
        <button
          data-testid="setup-student-view"
          onClick={openStudentView}
          disabled={!step1}
          className="btn-teal px-4 py-2 rounded-lg flex items-center gap-2 text-sm disabled:opacity-40"
        >
          <Eye size={16} />
          Open student view
        </button>
      ),
    },
  ];

  return (
    <div className="card-surface border border-flow-blue/30 p-6" data-testid="setup-card">
      <h3 className="font-heading text-lg font-semibold text-white">
        Welcome to DraftFlow{profile?.full_name ? `, ${profile.full_name.split(' ')[0]}` : ''} — three steps and your class is writing
      </h3>
      <div className="mt-5 space-y-5">
        {steps.map((step) => (
          <div key={step.n} className="flex items-start gap-4">
            {step.done ? (
              <CheckCircle2 className="text-teal-flow flex-shrink-0 mt-0.5" size={24} aria-label={`Step ${step.n} complete`} />
            ) : (
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-flow-blue/20 text-flow-blue-hover flex items-center justify-center text-sm font-bold">
                {step.n}
              </span>
            )}
            <div className="flex-1 min-w-0">
              <p className={`font-medium ${step.done ? 'text-slate-500' : 'text-white'}`}>{step.title}</p>
              {!step.done && (
                <>
                  <p className="text-slate-400 text-sm mt-1">{step.detail}</p>
                  <div className="mt-3">{step.action}</div>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SetupCard;
